import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Save, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { disponibilidadService } from '../services/disponibilidadService';
import { 
  FranjaHoraria, 
  CreateMultipleDisponibilidadDto, 
  DisponibilidadesPorDia 
} from '../types';
import { Card, Button, Input } from '../components/ui';

/**
 * Página para gestionar disponibilidades del entrenador
 */
export const Disponibilidad: React.FC = () => {
  const { user } = useAuth();
  const [diaSeleccionado, setDiaSeleccionado] = useState<'SATURDAY' | 'SUNDAY' | null>(null);
  const [franjasHorarias, setFranjasHorarias] = useState<FranjaHoraria[]>([]);
  const [disponibilidadesActuales, setDisponibilidadesActuales] = useState<DisponibilidadesPorDia>({
    sabado: [],
    domingo: []
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  /**
   * Carga las disponibilidades actuales del entrenador
   */
  const cargarDisponibilidades = async () => {
    if (!user?.id) return;
    
    try {
      const disponibilidades = await disponibilidadService.obtenerDisponibilidadesSemanaActual(user.id);
      setDisponibilidadesActuales(disponibilidades);
    } catch (error) {
      console.error('Error al cargar disponibilidades:', error);
    }
  };

  /**
   * Selecciona un día y carga las franjas existentes
   */
  const seleccionarDia = (dia: 'SATURDAY' | 'SUNDAY') => {
    setDiaSeleccionado(dia);
    setErrorMessage('');
    
    // Cargar franjas existentes para ese día
    const disponibilidadesDia = dia === 'SATURDAY' 
      ? disponibilidadesActuales.sabado 
      : disponibilidadesActuales.domingo;
    
    const franjasExistentes = disponibilidadesDia.map(d => ({
      horaInicio: d.horaInicio,
      horaFin: d.horaFin
    }));
    
    setFranjasHorarias(franjasExistentes);
  };

  /**
   * Añade una nueva franja horaria
   */
  const añadirFranja = () => {
    setFranjasHorarias([...franjasHorarias, { horaInicio: '', horaFin: '' }]);
  };

  /**
   * Elimina una franja horaria
   */
  const eliminarFranja = (index: number) => {
    const nuevasFranjas = franjasHorarias.filter((_, i) => i !== index);
    setFranjasHorarias(nuevasFranjas);
  };

  /**
   * Actualiza una franja horaria
   */
  const actualizarFranja = (index: number, campo: 'horaInicio' | 'horaFin', valor: string) => {
    const nuevasFranjas = [...franjasHorarias];
    nuevasFranjas[index][campo] = valor;
    setFranjasHorarias(nuevasFranjas);
  };

  /**
   * Valida las franjas horarias
   */
  const validarFranjas = (): boolean => {
    for (const franja of franjasHorarias) {
      if (!franja.horaInicio || !franja.horaFin) {
        setErrorMessage('Todas las franjas deben tener hora de inicio y fin');
        return false;
      }
      
      if (franja.horaInicio >= franja.horaFin) {
        setErrorMessage('La hora de inicio debe ser anterior a la hora de fin');
        return false;
      }
    }
    
    return true;
  };

  /**
   * Guarda las disponibilidades
   */
  const guardarDisponibilidades = async () => {
    if (!user?.id || !diaSeleccionado) return;
    
    if (!validarFranjas()) return;
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      const data: CreateMultipleDisponibilidadDto = {
        diaSemana: diaSeleccionado,
        entrenadorId: user.id,
        franjasHorarias: franjasHorarias.filter(f => f.horaInicio && f.horaFin)
      };
      
      await disponibilidadService.crearDisponibilidadesMultiples(data);
      await cargarDisponibilidades();
      setDiaSeleccionado(null);
      setFranjasHorarias([]);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Error al guardar disponibilidades';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Vuelve a la selección de días
   */
  const volverASeleccion = () => {
    setDiaSeleccionado(null);
    setFranjasHorarias([]);
    setErrorMessage('');
  };

  useEffect(() => {
    cargarDisponibilidades();
  }, [user?.id]);

  if (!diaSeleccionado) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center mb-8">
          <Calendar className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-secondary-50 mb-2">
            Marcar Disponibilidad
          </h1>
          <p className="text-secondary-400">
            Selecciona el día para configurar tus horarios disponibles
          </p>
        </div>

        <div className="space-y-4">
          <Card variant="glass" className="p-6">
            <h2 className="text-lg font-semibold text-slate-100 mb-4">
              Disponibilidades Actuales
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-slate-300 mb-2">Sábado</h3>
                {disponibilidadesActuales.sabado.length > 0 ? (
                  <div className="space-y-2">
                    {disponibilidadesActuales.sabado.map((d, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-slate-400">
                        <Clock className="w-4 h-4 text-emerald-400" />
                        {formatearHora(d.horaInicio)} - {formatearHora(d.horaFin)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Sin disponibilidades</p>
                )}
              </div>
              
              <div>
                <h3 className="font-medium text-slate-300 mb-2">Domingo</h3>
                {disponibilidadesActuales.domingo.length > 0 ? (
                  <div className="space-y-2">
                    {disponibilidadesActuales.domingo.map((d, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-slate-400">
                        <Clock className="w-4 h-4 text-emerald-400" />
                        {formatearHora(d.horaInicio)} - {formatearHora(d.horaFin)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Sin disponibilidades</p>
                )}
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <Button
              onClick={() => seleccionarDia('SATURDAY')}
              variant="primary"
              size="lg"
              className="h-20 flex flex-col items-center justify-center"
            >
              <Calendar className="w-6 h-6 mb-2" />
              Sábado
            </Button>
            
            <Button
              onClick={() => seleccionarDia('SUNDAY')}
              variant="primary"
              size="lg"
              className="h-20 flex flex-col items-center justify-center"
            >
              <Calendar className="w-6 h-6 mb-2" />
              Domingo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          onClick={volverASeleccion}
          variant="outline"
          size="sm"
        >
          ← Volver
        </Button>
        <h1 className="text-xl font-bold text-secondary-50">
          Configurar {diaSeleccionado === 'SATURDAY' ? 'Sábado' : 'Domingo'}
        </h1>
      </div>

      {errorMessage && (
        <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">
          {errorMessage}
        </div>
      )}

      <Card variant="glass" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">
            Franjas Horarias
          </h2>
          <div className="flex items-center gap-2">
            <Button
              onClick={añadirFranja}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border-white/20 text-slate-300 hover:bg-white/5 hover:text-white"
            >
              <Plus className="w-4 h-4" />
              Añadir
            </Button>
          </div>
        </div>

        {franjasHorarias.length === 0 ? (
          <p className="text-sm text-slate-500">
            Este día quedará sin horas disponibles hasta que añadas una franja.
          </p>
        ) : (
          <div className="space-y-4">
            {franjasHorarias.map((franja, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    type="time"
                    value={franja.horaInicio}
                    onChange={(e) => actualizarFranja(index, 'horaInicio', e.target.value)}
                    placeholder="Hora inicio"
                    className="bg-slate-900/40 border-white/10 text-slate-200 focus:border-emerald-500/50"
                  />
                </div>
                
                <span className="text-slate-500">-</span>
                
                <div className="flex-1">
                  <Input
                    type="time"
                    value={franja.horaFin}
                    onChange={(e) => actualizarFranja(index, 'horaFin', e.target.value)}
                    placeholder="Hora fin"
                    className="bg-slate-900/40 border-white/10 text-slate-200 focus:border-emerald-500/50"
                  />
                </div>
                
                <Button
                  onClick={() => eliminarFranja(index)}
                  variant="outline"
                  size="sm"
                  className="text-red-400 hover:text-red-300 border-white/10 hover:bg-red-500/10 hover:border-red-500/30"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex gap-4">
        <Button
          onClick={volverASeleccion}
          variant="outline"
          className="flex-1"
        >
          Cancelar
        </Button>
        
        <Button
          onClick={guardarDisponibilidades}
          variant="primary"
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Guardar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

/**
 * Formatea una hora para mostrar solo HH:MM (sin segundos)
 */
const formatearHora = (hora: string): string => {
  if (!hora) return '';
  // Si la hora viene con segundos (HH:MM:SS), tomar solo HH:MM
  return hora.substring(0, 5);
};

export default Disponibilidad;
